// paletaDesen.ts — UNICA paletă a desenelor.
//
// Tot ce se DESENEAZĂ (scena 3D, planurile generate, schițele de armare,
// diagramele de fundație, barele de încărcări) își ia culoarea de aici.
// Înainte fiecare pagină și-o scria singură, dintr-un limbaj rece născut
// înainte de tokenurile paletei: roșu-semafor #e04b3a la un pas, #c0392b la
// altul, albastru #3a76c4 / #2471a3 / #3949ab / #2c3e70 pentru „același"
// albastru, fundaluri #eef1f5 / #f2f3f5 / #f4f7fb și chenare #e3e7ee /
// #d5dbe5 / #dbe2ee. Rezultatul: aceeași grindă era altă culoare în 3D, în
// plan și în secțiune („adu și restul paginilor în sync").
//
// Valorile sunt cele ale interfeței (v. tokenurile din `app.css` și planșele
// din `mock-ui/design`). CULORILE LAYERELOR GENERATE se dublează în Python
// (`arhitectura.py`, `model.py`) fiindcă desenele se produc pe server —
// `tests/test_paleta_desen.py` verifică să nu se despartă.

export const PAL = {
  // CONVENȚIA E CEA A PROGRAMELOR DE CALCUL: cadrul e ALBASTRU, pereții
  // structurali de beton sunt ROȘII, plăcile sunt gri translucid, iar
  // fundalul e aproape alb și RECE. Nu e o preferință de gust — e limbajul
  // pe care un structurist îl citește deja din ETABS și SAP: albastru =
  // element de bară, roșu = element de suprafață care ține clădirea, gri =
  // ce nu ține. Un desen care folosește alt limbaj îl pune să învețe o
  // legendă în loc să se uite la model.
  //
  // Familiile se deosebesc prin NUANȚĂ în interiorul aceleiași convenții:
  // stâlpul e albastrul adânc, grinda cel deschis — amândouă fără dubiu
  // „bară". Ce e NOTAȚIE (`selectat`, `masa`, verdictele) stă în afara
  // convenției, ca un marcaj calculat să nu poată fi confundat cu materie.
  stalp: "#1b4a99",          // albastrul adânc al cadrului
  grinda: "#4a86d0",         // același albastru, deschis
  placa: "#8f9aa6",          // gri-ardezie: planșeul se vede prin el
  pereteBeton: "#cc3b30",    // roșul pereților structurali
  zidarieConfinata: "#b68e7c", // zidăria STRUCTURALĂ (confinată): tot perete
                             // care ține clădirea, deci din familia roșului,
                             // dar de cărămidă — nu se confundă cu betonul.
                             // Nuanța e STINSĂ (saturație ~28 %): zidăria
                             // ține doar etajul-proeminență, nu e piesa
                             // principală a scenei, iar un portocaliu plin
                             // sărea în ochi înaintea stâlpilor și pereților
  stalpisorZc: "#7d5d4f",    // STÂLPIȘORII zidăriei confinate: aceeași
                             // cărămidă, mai închisă. Sunt beton, dar NU sunt
                             // elemente verticale ale soluției (ESV) — sunt
                             // partea de confinare a peretelui. Desenați în
                             // albastrul stâlpului se citeau ca o stivă de
                             // stâlpi pusă de solver, adică ca un bug
  balconBeton: "#7d94ab",    // consolă de beton — balcon sau terasă: tot
                             // planșeu, alt gri-albastru
  logie: "#b4bbc2",          // gri rece: nu ține structura
  zidarie: "#b4bbc2",
  // NUCLEUL clădirii (casa scării, puțul liftului): context din arhitectură,
  // nu materie de calcul — două nuanțe potolite, în afara convenției de calcul
  scara: "#6f9a6a",          // verde stins: casa scării
  lift: "#b89a5a",           // ocru: puțul liftului
  selectat: "#f2c14e",       // aur: nu e culoarea niciunei familii
  masa: "#893bbf",           // centrul de masă: marcaj CALCULAT. Violet fiindcă
                             // e singura nuanță pe care convenția n-o
                             // folosește — nu se poate citi nici ca bară, nici
                             // ca perete structural
  nodFem: "#5b6672",
  nodReazem: "#c98a12",

  // ---- armătura: o culoare per familie de bare ---------------------------
  barSus: "#b5323d",
  barJos: "#2b5f8a",
  barSupl: "#a97614",        // suplimentarele de reazem
  barSuplCamp: "#2e7d72",    // suplimentarele de câmp
  etrieri: "#232323",        // și conturul de beton al secțiunii
  agrafe: "#6b4fa1",
  tirant: "#a97614",
  rost: "#b8730c",

  // ---- direcțiile de armare / de calcul ---------------------------------
  dirX: "#b5323d",
  dirY: "#2e7d72",

  // ---- verdictele, pentru cifrele și hașurile din desene ----------------
  ok: "#4f8207",
  bad: "#b5323d",
  amb: "#a97614",
  met: "#6b4fa1",
  acc: "#0b7f92",

  // ---- materialele în secțiune ------------------------------------------
  beton: "#ececec",
  betonLinie: "#858585",
  pamant: "#e6ded1",
  pamantLinie: "#b9ab93",
  fundatie: "#7b8896",       // betonul de sub cotă: ardezie deschisă, se retrage
  fundatieLinie: "#a97614",
  apa: "#3d7cab",

  // ---- hârtia, liniile, cerneala ----------------------------------------
  hartie: "#f5f6f8",
  fundal: "#f3f7fa",         // fundalul canvasului 3D: aproape alb și RECE,
                             // ca al unui viewport de program de calcul — pe
                             // un fundal cald, orice culoare potolită vira în sepia
  grilaMare: "#c3ccd6",      // grila scenei 3D, rece
  grilaMica: "#dde4ea",      // (doar scena 3D — hairline-urile UI sunt `linie`)
  linie: "#dedede",
  linie2: "#c8c8c8",
  ink: "#232323",
  mut: "#737373",
} as const;

/** Culorile LAYERELOR pe care le generează serverul în previzualizările de
 *  plan (Pașii 2 și 4). Se dublează în Python — v. antetul. */
export const PAL_LAYERE = {
  // aceleași culori ca familiile din `PAL`: stâlpul din plan și stâlpul din
  // 3D sunt același stâlp
  stalpi: "#1b4a99",
  grinzi: "#4a86d0",
  baza: "#8f9aa6",
  // pereții și plăcile de balcon / în consolă au culoarea LOR, nu pe a unui
  // token al temei: pe verdele lui `ok` un perete s-ar citi ca verdict și s-ar
  // topi în punctul de lipire din DwgViewer, pe accent o placă ar semăna cu
  // selecția. O retușare a temei nu le mută.
  peretiSimplificati: "#2e7d4f",
  peretiPersistenti: "#2e7d72",
  // golurile și marginile se desenează ȘI în scena 3D (v. `PAL_LAYERE_3D`),
  // deci intră în aceeași bandă potolită ca materialele din `PAL`
  usi: "#9d8fb5",
  ferestre: "#5fa8d3",
  terase: "#8b6f5c",
  balcoane: "#c05a33",
  logii: "#b4bbc2",
  consola: "#c05a33",
  margine: "#7e8b96",
} as const;

/** Terenul, strat cu strat: culoarea, SEMNUL și densitatea per clasă de
 *  terasament (cheile sunt cele din `sapatura.CLASE_TEREN`, pe server).
 *
 *  Culorile sunt pământii, ca nimic din teren să nu poată fi confundat cu un
 *  element de structură. Dar culoarea singură nu ajunge: un studiu dă des
 *  cinci straturi de argilă una peste alta, iar prisma iese un bloc uniform
 *. De aceea fiecare clasă are SEMNUL ei — exact
 *  convenția hașurilor din secțiunile geotehnice:
 *
 *    argilă  → liniuțe orizontale        nisip   → puncte rotunde
 *    praf    → liniuță + punct           pietriș → cerculețe
 *    umplutură → semne amestecate        rocă    → cruciulițe
 *
 *  `puncte_m3` e densitatea de bază (un pământ mai îndesat se vede mai dens),
 *  modulată în scenă cu greutatea volumică din studiu (γ/19); `marime_m` e cât
 *  de mare se desenează semnul, în metri — o liniuță are nevoie de mai mult
 *  loc decât un punct, deci vine cu o densitate mai mică. */
export type SemnTeren =
  | "puncte" | "linii" | "linii_duble" | "linie_punct" | "cercuri"
  | "amestec" | "cruci";

export const PAL_TEREN: Record<string, {
  culoare: string; puncte_m3: number; marime_m: number; semn: SemnTeren;
}> = {
  umplutura:       { culoare: "#9a8f7a", puncte_m3: 4.5, marime_m: 0.16,
                     semn: "amestec" },
  necoeziv:        { culoare: "#dcc38a", puncte_m3: 11,  marime_m: 0.08,
                     semn: "puncte" },
  nisip_prafos:    { culoare: "#cbb07a", puncte_m3: 8,   marime_m: 0.15,
                     semn: "linie_punct" },
  coeziv_redusa:   { culoare: "#c2a06f", puncte_m3: 6,   marime_m: 0.18,
                     semn: "linii" },
  coeziv_mijlocie: { culoare: "#b08256", puncte_m3: 6.5, marime_m: 0.20,
                     semn: "linii" },
  coeziv_mare:     { culoare: "#96603a", puncte_m3: 7,   marime_m: 0.22,
                     semn: "linii_duble" },
  roca:            { culoare: "#8a8474", puncte_m3: 9,   marime_m: 0.16,
                     semn: "cruci" },
};

/** Săpătura: portocaliu, transparentă. Nu e materie — e golul din care s-a
 *  scos pământ —, deci nu împrumută culoarea niciunui material. */
export const PAL_SAPATURA = "#d9a21b";

/** O culoare a paletei, translucidă: planșeul prin care se văd stâlpii, peretele
 *  de beton hașurat ușor peste cadru. */
export function alfa(hex: string, a: number): string {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return `rgba(${r},${g},${b},${a})`;
}

/** Aceleași culori ca numere, pentru three.js. */
function hexNr(h: string): number {
  return parseInt(h.slice(1), 16);
}
export const PAL3D: Record<keyof typeof PAL, number> = Object.fromEntries(
  Object.entries(PAL).map(([k, v]) => [k, hexNr(v)])
) as Record<keyof typeof PAL, number>;

/**
 * …și layerele generate, tot ca numere. Scena 3D desenează ACELEAȘI piese ca
 * previzualizarea de plan a serverului — ferestre, uși, parapete —, deci le ia
 * culoarea de acolo, nu una nouă. Altfel aceeași fereastră ar fi albastră în
 * plan și altceva în 3D, exact despărțirea pe care o păzește
 * `tests/test_paleta_desen.py`.
 */
export const PAL_LAYERE_3D: Record<keyof typeof PAL_LAYERE, number> =
  Object.fromEntries(
    Object.entries(PAL_LAYERE).map(([k, v]) => [k, hexNr(v)])
  ) as Record<keyof typeof PAL_LAYERE, number>;
